<template>
    <NuxtRouteAnnouncer />
    <LazyCommonLoader />
    <div class="h-screen">
        <NuxtLayout>
            <NuxtPage />
        </NuxtLayout>
    </div>
</template>

<script setup lang="ts">
import type { CmsItem } from '~/types/cms';

const nuxtApp = useNuxtApp();
const { public: publicConfig } = useRuntimeConfig();
const route = useRoute();
const googleTagManagerId = publicConfig.googleTagManagerId;

if (googleTagManagerId) {
    const searchParams = new URLSearchParams();

    for (const [key, value] of Object.entries(route.query)) {
        if (typeof value === 'string') {
            searchParams.set(key, value);
        }
    }

    const previewParams = getGoogleTagManagerQueryParamsFromSearch(searchParams);

    // Initialize dataLayer + consent defaults before GTM loads (matches GTM CookieScript CMP config)
    useHead({
        script: [
            {
                key: 'gtm-datalayer',
                innerHTML: `window.dataLayer = window.dataLayer || [];
function gtag(){dataLayer.push(arguments);}
gtag('consent', 'default', {
  analytics_storage: 'denied',
  ad_storage: 'denied',
  ad_user_data: 'denied',
  ad_personalization: 'denied',
  functionality_storage: 'denied',
  personalization_storage: 'denied',
  security_storage: 'denied',
  wait_for_update: 500
});`,
                type: 'text/javascript'
            }
        ],
        link: [
            { rel: 'preconnect', href: 'https://www.googletagmanager.com' },
            { rel: 'preconnect', href: 'https://www.google-analytics.com' }
        ],
        __dangerouslyDisableSanitizersByTagID: {
            'gtm-datalayer': ['innerHTML']
        }
    });

    useScriptGoogleTagManager({
        id: googleTagManagerId,
        auth: previewParams.gtm_auth,
        preview: previewParams.gtm_preview,
        debug: previewParams.gtm_debug,
        cookiesWin: previewParams.gtm_cookies_win,
        // Prevent CORS failure: @nuxt/scripts defaults gtm.js to crossorigin="anonymous",
        // which mismatches the (no-cors) preload/cache and gets blocked. Load it same-origin-less.
        scriptInput: {
            crossorigin: false
        }
    });
}

const menuData = useMenusData();
const settingsData = useSettingsData();
const categoriesData = useCategoriesData();

const loadComplete = useGlobalLoaded();
const pageComplete = usePageComplete();

const toggleMobileMenu = useToggleMobileMenu();
const isDesktop = useMediaQuery('(min-width: 1181px)');

nuxtApp.hook('app:suspense:resolve', () => {
    setTimeout(() => nuxtApp.$ScrollTrigger.refresh(true), 100);
});
nuxtApp.hook('page:loading:end', () => {
    nextTick(() => {
        loadComplete.value = true;
        document.querySelector('.lenis-content')?.classList.remove('is-global-loading');
    });
});

const siteId = useAvailableCraftLocales();

const [settingsResult, menusResult] = await Promise.all([
    useAsyncGql({
        operation: 'settings',
        variables: { siteId }
    }),
    useAsyncGql({
        operation: 'menus',
        variables: { siteId }
    })
]);

const { data: settings } = settingsResult;
const { data: menus } = menusResult;

// menus
menuData.value.menu_header = (menus.value?.mainmenuheader as CmsItem[]) ?? [];
menuData.value.menu_footer = (menus.value?.footermenu as CmsItem[]) ?? [];
menuData.value.menu_footer_information = (menus.value?.footerinformation as CmsItem[]) ?? [];

// settings
settingsData.value = (settings.value?.settings as CmsItem[]) ?? [];
categoriesData.value.news = (settings.value?.news as CmsItem[]) ?? [];
categoriesData.value.team = (settings.value?.team as CmsItem[]) ?? [];
categoriesData.value.folder = (settings.value?.folder as CmsItem[]) ?? [];
categoriesData.value.faq = (settings.value?.faq as CmsItem[]) ?? [];

watch(
    () => pageComplete.value,
    state => {
        nextTick(() => {
            useLenis(state);
            useHeader(state);
            useDataSpeed();
        });
    }
);

watch(
    () => isDesktop.value,
    state => {
        nextTick(() => nuxtApp.$ScrollTrigger.refresh(true));
        if (state) {
            nextTick(() => {
                toggleMobileMenu.value = false;
            });
        }
    }
);
</script>
